import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import { whatsappLink } from "@/constants/site";

interface QuickService {
  id: string;
  label: string;
  icon: string;
}

const QUICK_SERVICES: QuickService[] = [
  { id: "sites", label: "Criação de Sites", icon: "ri-layout-masonry-line" },
  { id: "logotipo", label: "Criação de Logotipo", icon: "ri-palette-line" },
  { id: "loja", label: "Loja Virtual", icon: "ri-shopping-bag-3-line" },
  { id: "hospedagem", label: "Hospedagem", icon: "ri-server-line" },
  { id: "vps", label: "VPS & Servidores", icon: "ri-hard-drive-3-line" },
  { id: "seo", label: "SEO", icon: "ri-search-eye-line" },
  { id: "marketing", label: "Marketing Digital", icon: "ri-megaphone-line" },
  { id: "googleads", label: "Google Ads", icon: "ri-google-fill" },
  { id: "landing", label: "Landing Page", icon: "ri-pages-line" },
  { id: "dominio", label: "Domínio e E-mail", icon: "ri-mail-send-line" },
];

export default function SolucoesQuickQuote() {
  const [selected, setSelected] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const chosen = useMemo(
    () => QUICK_SERVICES.filter((service) => selected.includes(service.id)),
    [selected],
  );

  const message = useMemo(() => {
    const lines = ["Ola! Gostaria de um orcamento rapido da NXHOST."];
    if (chosen.length) {
      lines.push("", "Servicos de interesse:");
      chosen.forEach((service) => lines.push(`- ${service.label}`));
    }
    if (name.trim()) lines.push("", `Nome/empresa: ${name.trim()}`);
    if (notes.trim()) lines.push("", `Detalhes: ${notes.trim()}`);
    return lines.join("\n");
  }, [chosen, name, notes]);

  const hasSelection = selected.length > 0;

  return (
    <section className="bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHeading
          badge="Orçamento rápido"
          badgeIcon="ri-flashlight-line"
          badgeTone="accent"
          title="Solicite um orçamento rápido"
          description="Marque os serviços que você precisa, deixe seus dados e envie tudo pronto pelo WhatsApp. É rápido e sem compromisso."
        />

        <Reveal delay={80}>
          <div className="mx-auto mt-12 max-w-4xl rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                Selecione os serviços
              </p>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-checkbox-multiple-line" aria-hidden="true"></i>
                {selected.length} selecionado{selected.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {QUICK_SERVICES.map((service) => {
                const active = selected.includes(service.id);
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => toggle(service.id)}
                    aria-pressed={active}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                      active
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-200 bg-background-100/60 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                    }`}
                  >
                    <i className={`${service.icon} text-lg`} aria-hidden="true"></i>
                    <span className="flex-1">{service.label}</span>
                    <i
                      className={
                        active ? "ri-checkbox-circle-fill text-lg" : "ri-checkbox-blank-circle-line text-lg opacity-40"
                      }
                      aria-hidden="true"
                    ></i>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                  Nome ou empresa
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Como podemos te chamar?"
                  className="rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </label>

              <label className="flex flex-col gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground-400">
                  Conte um pouco do projeto
                </span>
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value.slice(0, 500))}
                  maxLength={500}
                  rows={3}
                  placeholder="Opcional: objetivo, prazo, referências..."
                  className="resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                ></textarea>
                <span className="text-right text-[11px] text-foreground-400">{notes.length}/500</span>
              </label>
            </div>

            <div className="mt-6 flex flex-col items-center justify-between gap-4 border-t border-background-200 pt-6 sm:flex-row">
              <p className="text-sm text-foreground-500">
                {hasSelection
                  ? "Tudo pronto! Envie sua seleção direto para o nosso WhatsApp."
                  : "Marque ao menos um serviço para enviar o orçamento."}
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                {hasSelection ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSelected([]);
                      setName("");
                      setNotes("");
                    }}
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-eraser-line text-lg" aria-hidden="true"></i>
                    Limpar
                  </button>
                ) : null}
                <a
                  href={hasSelection ? whatsappLink(message) : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!hasSelection}
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors ${
                    hasSelection
                      ? "cursor-pointer bg-primary-500 hover:bg-primary-600"
                      : "pointer-events-none bg-primary-500/50"
                  }`}
                >
                  <i className="ri-whatsapp-line text-lg" aria-hidden="true"></i>
                  Enviar no WhatsApp
                </a>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}